import { useState } from 'react'
import { useGetAiUsageDateRangeQuery, useGetAiUsageByAgentQuery } from '../../store/api/usersApi'
import type { AiUsageByModelRow, UserAgentUsageRow } from '../../store/api/usersApi'
import { Card, StatCards, DateRangePicker, Modal, toYMD } from '../ui'

const fmt     = (n: number) => n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n)
const fmtCost = (n: number) => n < 0.0001 ? '$0.0000' : `${n.toFixed(4)}`
const REQUEST_TYPE_LABELS: Record<string, string> = { discussion: '🗨 Discussion', chat: '💬 Chat' }

function AgentRow({
  agentIcon, agentName, totalTokens, promptTokens, completionTokens,
  requestCount, costUsd, maxTokens, isLast,
}: {
  agentIcon: string | null
  agentName: string
  totalTokens: number
  promptTokens: number
  completionTokens: number
  requestCount: number
  costUsd: number
  maxTokens: number
  isLast: boolean
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.65rem 1rem', borderBottom: isLast ? 'none' : '1px solid var(--border)', flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '1 1 160px', minWidth: 0 }}>
        <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{agentIcon ?? '🤖'}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{agentName}</div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>
            {requestCount} req{requestCount !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
      <div style={{ flex: '2 1 140px', minWidth: '100px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{fmt(totalTokens)} tokens</span>
          <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{maxTokens > 0 ? Math.round((totalTokens / maxTokens) * 100) : 0}%</span>
        </div>
        <div style={{ height: 4, background: 'var(--bg-elevated)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ height: '100%', background: 'var(--accent-secondary, #06b6d4)', borderRadius: 2, width: `${maxTokens > 0 ? Math.min(100, (totalTokens / maxTokens) * 100) : 0}%`, transition: 'width 0.3s' }} />
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.3rem' }}>
          <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>in: {fmt(promptTokens)}</span>
          <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>out: {fmt(completionTokens)}</span>
        </div>
      </div>
      <div style={{ fontWeight: 700, fontSize: '0.85rem', color: costUsd > 0.01 ? 'var(--color-warning, #f59e0b)' : 'var(--text-secondary)', flexShrink: 0, minWidth: '4.5rem', textAlign: 'right' }}>
        {fmtCost(costUsd)}
      </div>
    </div>
  )
}

export function AiUsageModal({ onClose }: { onClose: () => void }) {
  const [from, setFrom] = useState(toYMD(new Date()))
  const [to,   setTo]   = useState(toYMD(new Date()))

  const { data, isFetching } = useGetAiUsageDateRangeQuery({ from, to })
  const { data: agentData, isFetching: agentFetching } = useGetAiUsageByAgentQuery({ from, to })

  const handleDate = (field: 'from' | 'to', val: string) => {
    if (field === 'from') setFrom(val)
    else setTo(val)
  }

  const totals    = data?.totals
  const byModel   = data?.byModel ?? []
  const maxTokens = byModel[0]?.totalTokens ?? 1

  const usedRows = agentData?.byAgent ?? []
  const maxUsed  = usedRows[0]?.totalTokens ?? 1

  return (
    <Modal onClose={onClose} maxWidth="820px" style={{ padding: 0, display: 'flex', flexDirection: 'column', maxHeight: '85vh', overflowY: 'hidden' }}>
      {/* These nested lists are small/easy to miss, so their scrollbar thumb is always
          visible — unlike the app-wide convention (globals.css) of only showing it on hover. */}
      <style>{`.au-scroll::-webkit-scrollbar-thumb { background: var(--scrollbar-thumb); }`}</style>

      {/* Fixed header — never scrolls */}
      <div style={{ flexShrink: 0, padding: '1.5rem 1.75rem 1rem', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.85rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text-primary)' }}>🤖 AI Usage</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: '1.1rem', cursor: 'pointer', lineHeight: 1, padding: '0.25rem' }}>✕</button>
        </div>

        {/* Page-level date range — governs both sections below */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <DateRangePicker from={from} to={to} onFromChange={val => handleDate('from', val)} onToChange={val => handleDate('to', val)} />
          {(isFetching || agentFetching) && <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Loading…</span>}
        </div>
      </div>

      {/* Scrollable body — holds both sections; each section also scrolls its own row list independently */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '1.25rem 1.75rem 1.75rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>

        {/* Overview card */}
        <Card>
          <div style={{ fontWeight: 600, fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '1rem' }}>My AI Usage</div>

          <StatCards
            style={{ marginBottom: '1.25rem' }}
            stats={[
              { icon: '🔢', label: 'Requests',      value: fmt(totals?.requestCount     ?? 0) },
              { icon: '📝', label: 'Prompt Tokens',  value: fmt(totals?.promptTokens     ?? 0) },
              { icon: '💬', label: 'Output Tokens',  value: fmt(totals?.completionTokens ?? 0) },
              { icon: '💰', label: 'Est. Cost',       value: fmtCost(totals?.costUsd     ?? 0) },
            ]}
          />

          {byModel.length === 0 && !isFetching && (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', padding: '1.5rem 0' }}>No usage data for this period.</div>
          )}
          {byModel.length > 0 && (
            <div className="au-scroll" style={{ border: '1px solid var(--border)', borderRadius: 10, maxHeight: '240px', overflowY: 'auto' }}>
              {byModel.map((row: AiUsageByModelRow, i: number) => (
                <div key={`${row.model}-${row.requestType}`}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.65rem 1rem', borderBottom: i < byModel.length - 1 ? '1px solid var(--border)' : 'none', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)' }}>{row.model}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>
                      {row.provider} · {REQUEST_TYPE_LABELS[row.requestType] ?? row.requestType}
                    </div>
                  </div>
                  <div style={{ flex: '2 1 140px', minWidth: '100px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{fmt(row.totalTokens)} tokens</span>
                      <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{row.requestCount} req{row.requestCount !== 1 ? 's' : ''}</span>
                    </div>
                    <div style={{ height: 4, background: 'var(--bg-elevated)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ height: '100%', background: 'var(--accent-primary)', borderRadius: 2, width: `${Math.min(100, (row.totalTokens / maxTokens) * 100)}%`, transition: 'width 0.3s' }} />
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.3rem' }}>
                      <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>in: {fmt(row.promptTokens)}</span>
                      <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>out: {fmt(row.completionTokens)}</span>
                    </div>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', color: row.costUsd > 0.01 ? 'var(--color-warning, #f59e0b)' : 'var(--text-secondary)', flexShrink: 0, minWidth: '4.5rem', textAlign: 'right' }}>
                    {fmtCost(row.costUsd)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Agents I interacted with */}
        <Card>
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontWeight: 600, fontSize: '1rem', color: 'var(--text-primary)' }}>🗣️ Agents I Interacted With</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>Agents that responded to your discussions or chats</div>
          </div>
          {agentFetching && <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', padding: '1.5rem 0' }}>Loading…</div>}
          {!agentFetching && usedRows.length === 0 && (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', padding: '1.5rem 0' }}>No agent interactions in this period.</div>
          )}
          {!agentFetching && usedRows.length > 0 && (
            <div className="au-scroll" style={{ border: '1px solid var(--border)', borderRadius: 10, maxHeight: '240px', overflowY: 'auto' }}>
              {usedRows.map((row: UserAgentUsageRow, i: number) => (
                <AgentRow key={row.agentId}
                  agentIcon={row.agentIcon} agentName={row.agentName}
                  totalTokens={row.totalTokens} promptTokens={row.promptTokens}
                  completionTokens={row.completionTokens} requestCount={row.requestCount}
                  costUsd={row.costUsd} maxTokens={maxUsed}
                  isLast={i === usedRows.length - 1}
                />
              ))}
            </div>
          )}
        </Card>
      </div>
    </Modal>
  )
}
